
import { Property, PropertyStatus, Invoice, User } from '../types';
import { jsPDF } from 'jspdf';

export const generateInvoice = (invoice: Invoice, user: User) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;

    // Colors
    const COLOR_DARK = '#0f172a';
    const COLOR_PRIMARY = '#10b981';
    const COLOR_GRAY = '#64748b';

    // --- HEADER ---
    doc.setFillColor(COLOR_DARK);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(COLOR_PRIMARY);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("DC PropChain", margin, 20);
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("INVOICE / RECEIPT", pageWidth - margin, 20, { align: 'right' });
    doc.text(`ID: ${invoice.id.substring(0,8).toUpperCase()}`, pageWidth - margin, 26, { align: 'right' });

    let y = 60;

    // --- DETAILS ---
    doc.setFontSize(10);
    doc.setTextColor(COLOR_GRAY);
    doc.text("BILLED TO:", margin, y);
    doc.text("ISSUED BY:", pageWidth / 2, y);

    y += 8;
    doc.setTextColor(COLOR_DARK);
    doc.setFont("helvetica", "bold");
    doc.text(user.name, margin, y);
    doc.text("DC PropChain Systems", pageWidth / 2, y);

    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLOR_GRAY);
    doc.text(user.email, margin, y);
    doc.text("NIF: 500 123 456", pageWidth / 2, y);
    doc.text("Lisbon, Portugal", pageWidth / 2, y + 6);

    y += 30;

    // --- LINE ITEMS ---
    doc.setFillColor('#f1f5f9');
    doc.rect(margin, y, pageWidth - (margin * 2), 10, 'F');
    doc.setTextColor(COLOR_DARK);
    doc.setFont("helvetica", "bold");
    doc.text("Description", margin + 5, y + 6.5);
    doc.text("Amount", pageWidth - margin - 5, y + 6.5, { align: 'right' });

    y += 18;
    doc.setFont("helvetica", "normal");
    doc.text(`${invoice.plan} Plan - ${invoice.billingPeriod === 'annual' ? 'Yearly' : 'Monthly'} Subscription`, margin + 5, y);
    doc.text(`EUR ${invoice.amount.toFixed(2)}`, pageWidth - margin - 5, y, { align: 'right' });

    y += 5;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageWidth - margin, y);

    // --- TOTALS ---
    y += 15;
    const vat = invoice.amount * 0.23; // 23% VAT PT
    const subtotal = invoice.amount - vat; // Assuming price is inclusive for simplicity in this demo, usually exclusive

    doc.text("Subtotal", pageWidth - margin - 50, y, { align: 'right' });
    doc.text(`EUR ${(invoice.amount / 1.23).toFixed(2)}`, pageWidth - margin - 5, y, { align: 'right' });
    
    y += 8;
    doc.text("VAT (23%)", pageWidth - margin - 50, y, { align: 'right' });
    doc.text(`EUR ${(invoice.amount - (invoice.amount / 1.23)).toFixed(2)}`, pageWidth - margin - 5, y, { align: 'right' });

    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("TOTAL PAID", pageWidth - margin - 50, y, { align: 'right' });
    doc.setTextColor(COLOR_PRIMARY);
    doc.text(`EUR ${invoice.amount.toFixed(2)}`, pageWidth - margin - 5, y, { align: 'right' });

    // --- STAMP ---
    doc.setDrawColor(COLOR_PRIMARY);
    doc.setLineWidth(1);
    doc.roundedRect(margin, y - 10, 40, 15, 2, 2);
    doc.setTextColor(COLOR_PRIMARY);
    doc.setFontSize(14);
    doc.text("PAID", margin + 8, y);
    
    // --- FOOTER ---
    doc.setFontSize(8);
    doc.setTextColor(COLOR_GRAY);
    doc.text("Thank you for your business.", margin, 270);
    doc.text(`Date Paid: ${new Date(invoice.date).toLocaleDateString()}`, margin, 275);

    doc.save(`DC_PropChain_Invoice_${invoice.date.split('T')[0]}.pdf`);
};

export const generatePropertyMemo = (property: Property, userName: string = "Investor") => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  
  // Colors
  const COLOR_PRIMARY = '#10b981'; // Emerald 500
  const COLOR_DARK = '#0f172a'; // Slate 900
  const COLOR_GRAY = '#64748b'; // Slate 500
  const COLOR_LIGHT = '#f1f5f9'; // Slate 100

  let yPos = 0;

  // --- HELPERS ---
  const addHeader = (title: string = "Investment Memo") => {
      // Top Bar
      doc.setFillColor(COLOR_DARK);
      doc.rect(0, 0, pageWidth, 15, 'F');
      
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text(`DC PropChain // ${title}`, margin, 10);
      
      doc.setFont("helvetica", "normal");
      doc.text(new Date().toLocaleDateString(), pageWidth - margin, 10, { align: 'right' });
  };

  const addFooter = (pageAttempt: number) => {
      doc.setFillColor(COLOR_LIGHT);
      doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
      
      doc.setFontSize(8);
      doc.setTextColor(COLOR_GRAY);
      doc.text(`Prepared for ${userName} • Confidential`, margin, pageHeight - 6);
      doc.text(`Page ${pageAttempt}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
  };

  // --- PAGE 1: COVER & OVERVIEW ---
  addHeader();
  yPos = 40;

  // Title Section
  doc.setTextColor(COLOR_PRIMARY);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("EXECUTIVE SUMMARY", margin, yPos);
  
  yPos += 10;
  doc.setTextColor(COLOR_DARK);
  doc.setFontSize(24);
  doc.text(property.address, margin, yPos);
  
  yPos += 8;
  doc.setTextColor(COLOR_GRAY);
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.text(`${property.city} • ${property.zipCode || 'Portugal'}`, margin, yPos);

  // Status Badge
  yPos += 15;
  doc.setDrawColor(COLOR_PRIMARY);
  doc.setFillColor(COLOR_PRIMARY);
  doc.roundedRect(margin, yPos, 40, 8, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(property.status.toUpperCase(), margin + 5, yPos + 5.5);
  
  doc.setDrawColor(COLOR_DARK); 
  doc.setTextColor(COLOR_DARK); 
  doc.setFillColor(255, 255, 255); 

  // Main Image Placeholder
  yPos += 20;
  doc.setFillColor(COLOR_LIGHT);
  doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 100, 2, 2, 'F');
  
  doc.setTextColor(COLOR_GRAY);
  doc.setFontSize(12);
  doc.text("Asset Photography", pageWidth / 2, yPos + 50, { align: 'center' });
  
  yPos += 115;

  // Key High-Level Metrics
  const annualIncome = property.monthlyRent * 12;
  const annualExpenses = property.expenses * 12;
  const noi = annualIncome - annualExpenses;
  const capRate = property.purchasePrice > 0 ? (noi / property.purchasePrice) * 100 : 0;
  const equity = property.currentValue - (property.loanAmount || 0);

  const metricsY = yPos;
  const colWidth = (pageWidth - (margin * 2)) / 3;

  // Metric 1
  doc.setFontSize(10);
  doc.setTextColor(COLOR_GRAY);
  doc.text("Current Valuation", margin, metricsY);
  doc.setFontSize(18);
  doc.setTextColor(COLOR_DARK);
  doc.setFont("helvetica", "bold");
  doc.text(`EUR ${property.currentValue.toLocaleString()}`, margin, metricsY + 8);

  // Metric 2
  doc.setFontSize(10);
  doc.setTextColor(COLOR_GRAY);
  doc.setFont("helvetica", "normal");
  doc.text("Net Equity", margin + colWidth, metricsY);
  doc.setFontSize(18);
  doc.setTextColor(COLOR_PRIMARY);
  doc.setFont("helvetica", "bold");
  doc.text(`EUR ${equity.toLocaleString()}`, margin + colWidth, metricsY + 8);

  // Metric 3
  doc.setFontSize(10);
  doc.setTextColor(COLOR_GRAY);
  doc.setFont("helvetica", "normal");
  doc.text("Cap Rate", margin + (colWidth * 2), metricsY);
  doc.setFontSize(18);
  doc.setTextColor(COLOR_DARK);
  doc.setFont("helvetica", "bold");
  doc.text(`${capRate.toFixed(2)}%`, margin + (colWidth * 2), metricsY + 8);

  addFooter(1);

  // --- PAGE 2: FINANCIAL ANALYSIS ---
  doc.addPage();
  addHeader();
  yPos = 40;

  doc.setTextColor(COLOR_PRIMARY);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("FINANCIAL PERFORMANCE", margin, yPos);
  
  yPos += 15;

  // Table Header
  doc.setFillColor(COLOR_LIGHT);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
  doc.setTextColor(COLOR_DARK);
  doc.text("Item", margin + 5, yPos + 6.5);
  doc.text("Monthly", pageWidth - margin - 50, yPos + 6.5, { align: 'right' });
  doc.text("Annual", pageWidth - margin - 5, yPos + 6.5, { align: 'right' });
  
  yPos += 18;

  // Rows
  const addRow = (label: string, monthly: number, isBold: boolean = false, color: string = COLOR_DARK) => {
      doc.setFont("helvetica", isBold ? "bold" : "normal");
      doc.setTextColor(color);
      doc.setFontSize(11);
      
      doc.text(label, margin + 5, yPos);
      doc.text(`€${monthly.toLocaleString()}`, pageWidth - margin - 50, yPos, { align: 'right' });
      doc.text(`€${(monthly * 12).toLocaleString()}`, pageWidth - margin - 5, yPos, { align: 'right' });
      
      // Line
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, yPos + 3, pageWidth - margin, yPos + 3);
      
      yPos += 12;
  };

  addRow("Gross Rental Income", property.monthlyRent);
  addRow("Vacancy Allowance (5%)", -(property.monthlyRent * 0.05), false, '#ef4444');
  addRow("Operating Expenses", -property.expenses, false, '#ef4444');
  
  yPos += 5;
  // NOI Row
  doc.setFillColor(COLOR_LIGHT);
  doc.rect(margin, yPos - 8, pageWidth - (margin * 2), 14, 'F');
  doc.setTextColor(COLOR_DARK);
  doc.setFont("helvetica", "bold");
  doc.text("Net Operating Income (NOI)", margin + 5, yPos + 1);
  doc.setTextColor(COLOR_PRIMARY);
  doc.text(`€${(noi/12).toLocaleString()}`, pageWidth - margin - 50, yPos + 1, { align: 'right' });
  doc.text(`€${noi.toLocaleString()}`, pageWidth - margin - 5, yPos + 1, { align: 'right' });

  yPos += 30;

  // Debt Service Section
  if (property.loanAmount && property.loanAmount > 0) {
      doc.setTextColor(COLOR_DARK);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Leverage Analysis", margin, yPos);
      yPos += 10;

      const rate = (property.interestRate || 0) / 100 / 12;
      const n = 30 * 12; // Assume 30y for simple projection
      const monthlyPmt = (property.loanAmount * rate) / (1 - Math.pow(1 + rate, -n));
      const annualDebt = monthlyPmt * 12;
      const cashFlow = noi - annualDebt;
      const cashOnCash = (property.purchasePrice - property.loanAmount) > 0 
          ? (cashFlow / (property.purchasePrice - property.loanAmount)) * 100 
          : 0;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(COLOR_GRAY);
      
      const debtBoxY = yPos;
      
      doc.text(`Total Debt: €${property.loanAmount.toLocaleString()}`, margin, yPos);
      doc.text(`Interest Rate: ${property.interestRate}%`, margin, yPos + 6);
      doc.text(`Est. Monthly Payment: €${monthlyPmt.toLocaleString(undefined, {maximumFractionDigits:0})}`, margin, yPos + 12);

      // ROI Box
      doc.setDrawColor(COLOR_PRIMARY);
      doc.rect(pageWidth / 2, debtBoxY - 5, (pageWidth/2) - margin, 35);
      
      doc.text("Cash Flow (Annual)", (pageWidth / 2) + 5, debtBoxY + 5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(cashFlow > 0 ? COLOR_PRIMARY : '#ef4444');
      doc.setFontSize(14);
      doc.text(`€${cashFlow.toLocaleString(undefined, {maximumFractionDigits:0})}`, (pageWidth / 2) + 5, debtBoxY + 12);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(COLOR_GRAY);
      doc.text("Cash-on-Cash Return", (pageWidth / 2) + 5, debtBoxY + 22);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(COLOR_DARK);
      doc.text(`${cashOnCash.toFixed(2)}%`, (pageWidth / 2) + 5, debtBoxY + 29);
  }

  addFooter(2);

  // --- SAVE ---
  doc.save(`DC_PropChain_Memo_${property.address.replace(/\s+/g, '_')}.pdf`);
};

export const generatePortfolioDossier = (properties: Property[], userName: string = "Investor") => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    
    // --- COLORS ---
    const COLOR_PRIMARY = '#10b981';
    const COLOR_DARK = '#0f172a';
    const COLOR_GRAY = '#64748b';
    const COLOR_LIGHT = '#f1f5f9';

    // --- AGGREGATE STATS ---
    const totalValue = properties.reduce((acc, p) => acc + p.currentValue, 0);
    const totalDebt = properties.reduce((acc, p) => acc + (p.loanAmount || 0), 0);
    const equity = totalValue - totalDebt;
    const annualRent = properties.reduce((acc, p) => acc + (p.monthlyRent * 12), 0);
    const ltv = totalValue > 0 ? (totalDebt / totalValue) * 100 : 0;
    const grossYield = totalValue > 0 ? (annualRent / totalValue) * 100 : 0;

    // --- PAGE 1: COVER ---
    doc.setFillColor(COLOR_DARK);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    doc.setTextColor(COLOR_PRIMARY);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("DC PropChain", margin, 40);
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(32);
    doc.text("PORTFOLIO", margin, 60);
    doc.text("REPORT", margin, 75);
    
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(200, 200, 200);
    doc.text(`${userName}`, margin, 100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, 110);

    // Cover Stats
    doc.setDrawColor(COLOR_PRIMARY);
    doc.line(margin, 130, pageWidth - margin, 130);
    
    doc.setFontSize(10);
    doc.text("TOTAL VALUATION", margin, 145);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(`EUR ${totalValue.toLocaleString()}`, margin, 155);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("NET EQUITY", margin, 175);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(`EUR ${equity.toLocaleString()}`, margin, 185);

    // --- PAGE 2: ASSET MATRIX ---
    doc.addPage();
    doc.setFillColor(COLOR_DARK);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text("PORTFOLIO BREAKDOWN", margin, 16);

    let y = 40;
    // Table Header
    doc.setFillColor(COLOR_LIGHT);
    doc.rect(margin, y, pageWidth - (margin * 2), 10, 'F');
    doc.setTextColor(COLOR_DARK);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    
    doc.text("Asset", margin + 5, y + 6.5);
    doc.text("Value", pageWidth - margin - 80, y + 6.5, { align: 'right' });
    doc.text("Rent (Mo)", pageWidth - margin - 40, y + 6.5, { align: 'right' });
    doc.text("Status", pageWidth - margin - 5, y + 6.5, { align: 'right' });

    y += 15;
    
    // Rows
    properties.forEach((p) => {
        if (y > pageHeight - 30) {
            doc.addPage();
            y = 40;
        }
        
        doc.setTextColor(COLOR_DARK);
        doc.setFont("helvetica", "bold");
        doc.text(p.address.substring(0, 25) + (p.address.length > 25 ? '...' : ''), margin + 5, y);
        
        doc.setFont("helvetica", "normal");
        doc.setTextColor(COLOR_GRAY);
        doc.text(p.city, margin + 5, y + 5);

        doc.setTextColor(COLOR_DARK);
        doc.text(`€${p.currentValue.toLocaleString()}`, pageWidth - margin - 80, y + 2.5, { align: 'right' });
        doc.text(`€${p.monthlyRent.toLocaleString()}`, pageWidth - margin - 40, y + 2.5, { align: 'right' });
        
        doc.setTextColor(p.status === 'Occupied' ? COLOR_PRIMARY : '#ef4444');
        doc.setFont("helvetica", "bold");
        doc.text(p.status, pageWidth - margin - 5, y + 2.5, { align: 'right' });

        doc.setDrawColor(230, 230, 230);
        doc.line(margin, y + 8, pageWidth - margin, y + 8);
        
        y += 15;
    });

    // Summary Box
    y += 10;
    doc.setFillColor(COLOR_LIGHT);
    doc.roundedRect(margin, y, pageWidth - (margin * 2), 30, 2, 2, 'F');
    
    doc.setTextColor(COLOR_DARK);
    doc.setFont("helvetica", "bold");
    doc.text(`Assets: ${properties.length}`, margin + 10, y + 10);
    doc.text(`LTV Ratio: ${ltv.toFixed(1)}%`, margin + 10, y + 20);
    
    doc.text(`Gross Yield: ${grossYield.toFixed(2)}%`, (pageWidth/2), y + 10);
    doc.text(`Annual Income: €${annualRent.toLocaleString()}`, (pageWidth/2), y + 20);

    // --- SAVE ---
    doc.save(`DC_PropChain_Portfolio_${new Date().toISOString().split('T')[0]}.pdf`);
}
